import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { almacen, type MovementKind } from '../api';
import { CABECERA_DE_CONTROLADOS, filasDeControlados } from '../botica';
import { Boton, Cabecera, Cargando, Fallo, Pastilla, Rotulo, Vacio } from '../componentes';
import { descargarCsv } from '../csv';
import { cantidad, cantidadConSigno, dia, diaDeLima, hora, MOVIMIENTO } from '../formato';

/** El movimiento en palabras; uno que esta pantalla no conoce, tal cual. */
function tipoDe(kind: string): string {
  return MOVIMIENTO[kind as MovementKind]?.texto ?? kind;
}

/**
 * El libro de controlados: cada entrada y salida de lo que la ficha marca
 * «Producto controlado», con su saldo, y el balance del periodo.
 *
 * Es el que pide la DIGEMID para psicotrópicos y estupefacientes. Sale del
 * kardex —no es otro registro que alguien tenga que llevar aparte—, así que
 * no tiene huecos: cada pastilla que entró o salió está aquí con su lote, su
 * documento y quién lo hizo. Ver docs/15 §2.56.
 *
 * **Antes de presentarlo en una inspección, el químico farmacéutico tiene que
 * confirmar que el libro electrónico se acepta**: si la norma pide el libro
 * foliado, esto sirve para llenarlo, no para reemplazarlo.
 */
export function LibroDeControlados() {
  const [desde, setDesde] = useState(() => `${diaDeLima().slice(0, 7)}-01`);
  const [hasta, setHasta] = useState(() => diaDeLima());

  const consulta = useQuery({
    queryKey: ['libro-de-controlados', desde, hasta],
    queryFn: () => almacen.controlledBook(desde, hasta),
    enabled: desde !== '' && hasta !== '' && desde <= hasta,
  });

  const movimientos = consulta.data?.movements ?? [];
  const saldos = consulta.data?.balances ?? [];

  return (
    <>
      <Cabecera
        titulo="Libro de controlados"
        subtitulo="Entradas, salidas y saldo de lo controlado, para quien inspecciona"
        ayuda="Lo que la ficha marca «Producto controlado». Sale del kardex: cada entrada y salida con su lote, su documento, su receta si fue una venta, y el saldo que dejó. Antes de presentarlo, que el químico farmacéutico confirme que el libro electrónico se acepta."
        acciones={
          <>
            <div className="campo campo--en-linea">
              <Rotulo htmlFor="controlados-desde" ayuda="El primer día, en hora de Lima.">
                Del
              </Rotulo>
              <input
                id="controlados-desde"
                type="date"
                value={desde}
                max={hasta}
                onChange={(e) => setDesde(e.target.value)}
              />
            </div>
            <div className="campo campo--en-linea">
              <Rotulo htmlFor="controlados-hasta" ayuda="El último día, incluido.">
                al
              </Rotulo>
              <input
                id="controlados-hasta"
                type="date"
                value={hasta}
                min={desde}
                max={diaDeLima()}
                onChange={(e) => setHasta(e.target.value)}
              />
            </div>
            <Boton
              onClick={() =>
                descargarCsv(
                  `libro-de-controlados-${desde}-a-${hasta}.csv`,
                  CABECERA_DE_CONTROLADOS,
                  filasDeControlados(movimientos, tipoDe),
                )
              }
              disabled={movimientos.length === 0}
            >
              Exportar
            </Boton>
          </>
        }
      />

      <div className="cuerpo">
        <p className="aviso-caja aviso-caja--aviso">
          Antes de presentarlo en una inspección, que el químico farmacéutico confirme que el
          libro electrónico se acepta. Si se pide el libro foliado, este sirve para llenarlo.
        </p>

        {consulta.isError ? (
          <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} />
        ) : null}

        {consulta.isLoading ? (
          <Cargando que="el libro de controlados" />
        ) : saldos.length === 0 ? (
          <Vacio
            titulo="Nada controlado se movió en esos días"
            mensaje="Marca «Producto controlado» en la ficha de lo que lo es. Desde entonces, cada entrada y salida queda aquí con su saldo."
          />
        ) : (
          <>
            <h2 className="tarjeta__titulo">Balance del periodo</h2>
            <div className="tabla-envoltura">
              <table>
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th className="numero">Saldo inicial</th>
                    <th className="numero">Entradas</th>
                    <th className="numero">Salidas</th>
                    <th className="numero">Saldo final</th>
                  </tr>
                </thead>
                <tbody>
                  {saldos.map((saldo) => (
                    <tr key={saldo.supplyId}>
                      <td>
                        <div className="celda-nombre__texto">
                          <strong>{saldo.productNames === '' ? saldo.supplyName : saldo.productNames}</strong>
                          <span>{saldo.supplyName}</span>
                        </div>
                      </td>
                      <td className="numero">{cantidad(saldo.opening)}</td>
                      <td className="numero">{cantidad(saldo.in)}</td>
                      <td className="numero">{cantidad(saldo.out)}</td>
                      <td className="numero">
                        <strong>{cantidad(saldo.closing)}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="tarjeta__titulo">Movimientos</h2>
            <div className="tabla-envoltura">
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Producto</th>
                    <th>Movimiento</th>
                    <th className="numero">Cantidad</th>
                    <th className="numero">Saldo</th>
                    <th>Lote</th>
                    <th>Documento</th>
                    <th>Receta</th>
                    <th>Responsable</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map((fila, indice) => (
                    <tr key={`${fila.occurredAt}-${indice}`}>
                      <td className="apagado mono">
                        {dia(fila.occurredAt)} {hora(fila.occurredAt)}
                      </td>
                      <td>
                        <strong>{fila.productNames === '' ? fila.supplyName : fila.productNames}</strong>
                      </td>
                      <td>
                        <Pastilla
                          texto={tipoDe(fila.kind)}
                          tono={MOVIMIENTO[fila.kind as MovementKind]?.tono ?? 'neutro'}
                        />
                      </td>
                      <td className="numero">{cantidadConSigno(fila.quantity)}</td>
                      <td className="numero">{cantidad(fila.balance)}</td>
                      <td className="mono">{fila.lotCode ?? 'Sin lote'}</td>
                      <td className="apagado mono">{fila.document ?? ''}</td>
                      <td>
                        <div className="celda-nombre__texto">
                          <strong>{fila.prescriptionNumber ?? '—'}</strong>
                          {fila.prescriber === null ? null : <span>{fila.prescriber}</span>}
                        </div>
                      </td>
                      <td className="apagado">{fila.performedBy ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}
